import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, retry, timer } from 'rxjs';
import { AlarmType } from '@core/models';
import { StatusService } from './status.service';

/**
 * Single source of truth for alarm-type display labels, mirroring the backend's
 * `shared.mapping.alarm_type_catalog` (exposed via GET /alarm-types) — including its NL/EN
 * labels (`label`/`label_nl`), so DLMS event names (e.g. fraud code 44 "Meter cover
 * removed"/"Meterdeksel verwijderd") don't need a second, drifting copy client-side. Raw
 * events are stored/keyed as-is (see xmx-dpf-alarm); this service is the one place that turns
 * a raw key into a label, in the user's current UI language.
 *
 * Fetched once per app lifetime and cached in a signal, so any computed() reading
 * `label()`/`priority()` re-evaluates automatically once the catalog arrives or the language
 * changes.
 */
@Injectable({
  providedIn: 'root',
})
export class AlarmTypeCatalogService {
  private readonly statusService = inject(StatusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  private readonly byKey = signal<Map<string, AlarmType>>(new Map());
  readonly loaded = computed(() => this.byKey().size > 0);
  private readonly currentLang = signal(this.translateService.currentLang);

  constructor() {
    this.statusService
      .getAlarmTypes()
      .pipe(
        map(collection => new Map(collection.items.map(type => [type.key, type]))),
        retry({ count: 3, delay: (_, attempt) => timer(attempt * 1000) }),
        catchError(() => of(new Map<string, AlarmType>())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(byKey => this.byKey.set(byKey));

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.currentLang.set(event.lang);
    });
  }

  /** Reactive lookup — call from inside a computed()/effect() so it re-evaluates once the
   * catalog loads or the UI language changes. Returns the raw key itself if
   * unloaded/unrecognized, so callers never show a blank cell while loading. Underscores are
   * turned into spaces either way — cloud types (`device_connection`) have no proper label of
   * their own yet (label === key) and an unrecognized key is shown as-is otherwise, so both
   * would otherwise leak raw snake_case into the UI. */
  label(key: string): string {
    if (!key) return key;
    const alarmType = this.byKey().get(this.normalize(key));
    if (!alarmType) return key.replace(/_/g, ' ');
    const label = (this.currentLang()?.startsWith('nl') ? alarmType.label_nl : alarmType.label) ?? key;
    return label.replace(/_/g, ' ');
  }

  priority(key: string): AlarmType['default_priority'] | undefined {
    if (!key) return undefined;
    return this.byKey().get(this.normalize(key))?.default_priority;
  }

  /** The device-connection and no-data-alarm event types are written by the backend with a
   * `device.rule.status.` prefix (e.g. `device.rule.status.device_connection`), while the
   * catalog keys them bare (`device_connection`). Every other alarm type already matches the
   * catalog as-is, so only strip this one known prefix rather than reshaping all keys. */
  private normalize(key: string): string {
    const prefix = 'device.rule.status.';
    return key.startsWith(prefix) ? key.slice(prefix.length) : key;
  }
}
